import ApiService from '@/core/services/ApiService';

/** Parámetro oficial de MaxiPublica para deep links. */
export const MAXI_PUBLICA_QUERY_KEY = 'mpId';

const FALLBACK_VEHICLE_ID_QUERY_KEYS = ['id', 'ref', 'vehicle', 'ad'] as const;

/**
 * Migración one-shot de bookmarks/links viejos en hash (`/#/car?...`) a history
 * (`/car?...`). Con createWebHistory, MaxiPublica path-style ya funciona nativo.
 *
 * @returns true si se disparó redirección (la página se recargará).
 */
export function redirectLegacyHashRouteToHistory(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const { hash, origin, search } = window.location;
  if (!hash || !hash.startsWith('#/')) {
    return false;
  }

  // `#/car?mpId=...` o `#/catalogo` → `/car?mpId=...` / `/catalogo`
  const hashBody = hash.slice(1); // quita '#'
  const qIndex = hashBody.indexOf('?');
  const pathPart = qIndex >= 0 ? hashBody.slice(0, qIndex) : hashBody;
  const hashQuery = qIndex >= 0 ? hashBody.slice(qIndex + 1) : '';

  // Preferir query del hash; si no hay, conservar search de la URL path.
  const mergedSearch = hashQuery
    ? `?${hashQuery}`
    : search || '';

  window.location.replace(`${origin}${pathPart}${mergedSearch}`);
  return true;
}

/** @deprecated Usar redirectLegacyHashRouteToHistory. Alias de compat. */
export function redirectPathStyleMaxiDeepLinkToHash(): boolean {
  return redirectLegacyHashRouteToHistory();
}

export function isInternalVehicleId(id: string): boolean {
  return /^\d+$/.test(id);
}

export function decodeMaxiIdFromUrl(encoded: string): string | null {
  const trimmed = encoded.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const normalized = trimmed.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4;
    const padded = padding > 0
      ? normalized + '='.repeat(4 - padding)
      : normalized;

    return atob(padded);
  } catch {
    return null;
  }
}

/**
 * Obtiene el id MaxiPublica codificado en base64 desde la ruta /car.
 * Soporta /car?mpId=... y /car/{mpId} según especificación MaxiPublica.
 */
export function getMaxiPublicaEncodedId(route: {
  path: string;
  query: Record<string, unknown>;
  params: Record<string, unknown>;
}): string | null {
  const pathMpId = route.params.mpId;
  if (typeof pathMpId === 'string' && pathMpId.trim() !== '') {
    return pathMpId.trim();
  }

  const queryMpId = route.query[MAXI_PUBLICA_QUERY_KEY];
  if (typeof queryMpId === 'string' && queryMpId.trim() !== '') {
    return queryMpId.trim();
  }

  return null;
}

export function getExternalVehicleEncodedId(
  query: Record<string, unknown>
): string | null {
  const mpId = query[MAXI_PUBLICA_QUERY_KEY];
  if (typeof mpId === 'string' && mpId.trim() !== '') {
    return mpId.trim();
  }

  for (const key of FALLBACK_VEHICLE_ID_QUERY_KEYS) {
    const value = query[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }

  return null;
}

export async function resolveVehicleIdFromRouteParam(
  rawId: string
): Promise<string | null> {
  if (!rawId || rawId === 'undefined') {
    return null;
  }

  if (isInternalVehicleId(rawId)) {
    return rawId;
  }

  try {
    const response: any = await ApiService.get(
      `/vehicles/resolve-maxi-id?ref=${encodeURIComponent(rawId)}`
    );
    const payload = response?.data?.data;
    const resolvedId = Array.isArray(payload)
      ? payload[0]?.IdVehicle
      : payload?.IdVehicle;

    return resolvedId != null ? String(resolvedId) : null;
  } catch {
    return null;
  }
}
